import { useState, useEffect, useCallback } from "react";
import { projectURL } from "./utils/mockdata";
import { CreateSubComment } from "./CreateSubComment";

export const ListSubComment = ({ commentId }) => {
    const [userSubComment, setUserSubComment] = useState([]);
    const [subCommentAdded, setSubCommentAdded] = useState(false);

    const loggedUserId = sessionStorage.getItem("loggedUserId");
    
    const [dropdownOpen, setDropdownOpen] = useState(null);    
    
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editCommentId, setEditCommentId] = useState(null);
    const [editContent, setEditContent] = useState("");

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteCommentId, setDeleteCommentId] = useState(null);    

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = () => setDropdownOpen(null);
        window.addEventListener("click", handleClickOutside);
        return () => window.removeEventListener("click", handleClickOutside);
    }, []);    

    const handleEdit = (commentId) => {
        const subcomment = userSubComment.find((p) => p._id === commentId);
        setEditCommentId(commentId);
        setEditContent(subcomment?.subcomment || "");
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        try {
            const res = await fetch(`${projectURL}subcomments/${editCommentId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ subcomment: editContent }),
            });

            if (res.ok) {
                const updatedTime = new Date().toISOString();

                setUserSubComment((prev) =>
                    prev.map((p) =>
                        p._id === editCommentId ? { ...p, subcomment: editContent, updated_at: updatedTime } : p
                    )
                );
                setIsEditModalOpen(false);
                setEditCommentId(null);
                setEditContent("");
            }
        } catch (err) {
            console.error("Update error:", err);
        }
    };


    const handleDeleteClick = (commentId) => {
        setDeleteCommentId(commentId);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        try {
        const res = await fetch(`${projectURL}subcomments/${deleteCommentId}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
        });

        if (res.ok) {
            setUserSubComment((prev) => prev.filter((p) => p._id !== deleteCommentId));
            setIsDeleteModalOpen(false);
            setDeleteCommentId(null);      
        }
        } catch (err) {
            // console.error("Delete error:", err);
        }
    };    

    const fetchSubComments = useCallback(async () => {
        const fetchSubCommentPath = projectURL + "subcomments/" + commentId;
        const subCommentList = await fetch(fetchSubCommentPath, {
            headers: { "cors": "no-cors" }
        });
        const subCommentJson = await subCommentList.json();
        setUserSubComment(subCommentJson.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    }, [commentId]);

    function timeAgo(dateString) {
        const now = new Date();
        const date = new Date(dateString);
        const seconds = Math.floor((now - date) / 1000);

        const intervals = {
            year: 31536000,
            month: 2592000,
            week: 604800,
            day: 86400,
            hour: 3600,
            minute: 60,
            second: 1,
        };

        for (const [unit, value] of Object.entries(intervals)) {
            const count = Math.floor(seconds / value);
            if (count >= 1) {
            return `${count} ${unit}${count > 1 ? 's' : ''} ago`;
            }
        }

        return "just now";
    }    

    useEffect(() => {
        fetchSubComments();
    }, [fetchSubComments, subCommentAdded]);

    return (
        <div>
            <CreateSubComment commentId={commentId} onSubCommentCreated={() => setSubCommentAdded(prev => !prev)} />
            {
                userSubComment?.map((data) => (
                    <div className="flex justify-center items-center" key={data?._id}>
                        <div className="relative flex flex-col my-1 bg-white shadow-sm border border-slate-400 rounded-lg w-full m-1 max-w-4xl">
                            <div className="flex items-center justify-between p-1">
                                <div className="flex items-center">
                                    <img
                                        alt="User Avatar"
                                        src={data?.created_by?.imagePath}
                                        className="relative inline-block h-6 w-6 rounded-full border border-blue-800"
                                    />
                                    <div className="flex flex-col ml-2 text-xs">
                                    <span className="font-medium text-blue-500">{data?.created_by?.username}</span>
                                    <span className="text-slate-400">{timeAgo(data?.created_at)}
                                        {data?.created_at === data?.updated_at ? "" : " | 🖊️edited"}
                                    </span>
                                    </div>
                                </div>

                                {/* Dropdown */}
                                {data?.created_by?._id === loggedUserId && (
                                <div className="relative" onClick={(e) => e.stopPropagation()}>
                                    <button
                                    onClick={() =>
                                        setDropdownOpen((prev) => (prev === data._id ? null : data._id))
                                    }
                                    className="text-gray-600 hover:text-gray-800 px-2 py-1 rounded-full"
                                    >
                                    ⋮
                                    </button>
                                    {dropdownOpen === data._id && (
                                    <div className="absolute right-2 w-28 bg-white border border-gray-300 rounded shadow-md z-10">
                                        <button
                                        onClick={() => handleEdit(data._id)}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                                        >
                                        Edit
                                        </button>
                                        <button
                                        onClick={() => handleDeleteClick(data._id)}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-500"
                                        >
                                        Delete
                                        </button>
                                    </div>
                                    )}
                                </div>
                                )}
                            </div>
                            
                            <hr />
                            <div className="p-2">
                                <p className="text-gray-700 whitespace-pre-line text-base">{data?.subcomment}</p>
                            </div>
                        </div>
                    </div>
                ))
            }


            {/* Edit Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-4 rounded-lg w-full max-w-md">
                    <h3 className="text-lg font-semibold mb-2">Edit Reply</h3>
                    <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                    className="w-full p-1 border border-gray-200 rounded"
                    />
                    <div className="flex justify-end mt-2 gap-2">
                    <button
                        onClick={() => setIsEditModalOpen(false)}
                        className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm"
                    >Cancel</button>
                    <button
                        onClick={handleSaveEdit}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >Save</button>
                    </div>
                </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-4 rounded-lg w-full max-w-md">
                    <h3 className="text-lg font-semibold mb-2">Are you sure you want to delete this reply?</h3>
                    <div className="flex justify-end mt-2 gap-2">
                    <button
                        onClick={() => setIsDeleteModalOpen(false)} // Close modal without deleting
                        className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm"
                    >Cancel</button>
                    <button
                        onClick={handleDelete} // Proceed with deletion
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >Delete</button>
                    </div>
                </div>
                </div>
            )}
        </div>
    );
}
export default ListSubComment;